import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { catchError, tap, map } from 'rxjs/operators';
import { SceneAssetRelation } from '../models/scene-asset-relations/SceneAssetRelation';

import { NgForm } from '@angular/forms';
import { AudioSceneAssetRelation } from '../models/scene-asset-relations/AudioSceneAssetRelation';
import { Model3DSceneAssetRelation } from '../models/scene-asset-relations/model3DSceneAssetRelation';
import { InvisibleSceneAssetRelation } from '../models/scene-asset-relations/InvisibleSceneAssetRelation';
import { ImageSceneAssetRelation } from '../models/scene-asset-relations/imageSceneAssetRelation';
import { VideoSceneAssetRelation } from '../models/scene-asset-relations/videoSceneAssetRelation';
import { QuestionSceneAssetRelation } from '../models/scene-asset-relations/questionSceneAssetRelation';
import { API_SETTINGS } from './API_SETTINGS';
import { InvisibleAsset } from '../models/assets/InvisibleAsset';
import { ZlibReset } from 'zlib';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};

const apiUrl = API_SETTINGS.API_URL + 'SceneAssetRelation';

@Injectable({
  providedIn: 'root'
})
export class ApiSceneAssetRelationService {

  constructor(private http: HttpClient) { }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {

      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  getSceneAssetRelations(): Observable<SceneAssetRelation[]> {
    const url = apiUrl + '/GetSceneAssetRelations';
    return this.http.get<SceneAssetRelation[]>(url).pipe();
  }

  getSceneAssetRelation(id: number): Observable<SceneAssetRelation> {
    const url = `${apiUrl}/GetSceneAssetRelation/${id}`;
    return this.http.get<SceneAssetRelation>(url).pipe();
  }

  addSceneAssetRelation(SceneAssetRelation: SceneAssetRelation): Observable<SceneAssetRelation> {
    const url = apiUrl + '/PostSceneAssetRelation';
    return this.http.post<SceneAssetRelation>(url, SceneAssetRelation, httpOptions).pipe();
  }

  addModel3DSceneAssetRelation(Model3DSceneAssetRelation: Model3DSceneAssetRelation): Observable<Model3DSceneAssetRelation> {
    const url = apiUrl + '/PostModel3DSceneAssetRelation';
    return this.http.post<Model3DSceneAssetRelation>(url, Model3DSceneAssetRelation, httpOptions).pipe();
  }

  addInvisibleSceneAssetRelation(InvisibleSceneAssetRelation: InvisibleSceneAssetRelation): Observable<InvisibleSceneAssetRelation> {
    const url = apiUrl + '/PostInvisibleSceneAssetRelation';
    return this.http.post<InvisibleSceneAssetRelation>(url, InvisibleSceneAssetRelation, httpOptions).pipe();
  }

  addAudioSceneAssetRelation(AudioSceneAssetRelation: AudioSceneAssetRelation): Observable<AudioSceneAssetRelation> {
    const url = apiUrl + '/PostAudioSceneAssetRelation';
    return this.http.post<AudioSceneAssetRelation>(url, AudioSceneAssetRelation, httpOptions).pipe();
  }

  addImageSceneAssetRelation(ImageSceneAssetRelation: ImageSceneAssetRelation): Observable<ImageSceneAssetRelation> {
    const url = apiUrl + '/PostImageSceneAssetRelation';
    return this.http.post<ImageSceneAssetRelation>(url, ImageSceneAssetRelation, httpOptions).pipe();
  }

  addVideoSceneAssetRelation(VideoSceneAssetRelation: VideoSceneAssetRelation): Observable<VideoSceneAssetRelation> {
    const url = apiUrl + '/PostVideoSceneAssetRelation';
    return this.http.post<VideoSceneAssetRelation>(url, VideoSceneAssetRelation, httpOptions).pipe();
  }

  addQuestionSceneAssetRelation(QuestionSceneAssetRelation: QuestionSceneAssetRelation): Observable<QuestionSceneAssetRelation> {
    const url = apiUrl + '/PostQuestionSceneAssetRelation';
    return this.http.post<QuestionSceneAssetRelation>(url, QuestionSceneAssetRelation, httpOptions).pipe();
  }

  updateSceneAssetRelation(id: number, SceneAssetRelation: any): Observable<SceneAssetRelation> {
    const url = `${apiUrl}/PutSceneAssetRelation/${id}`;
    return this.http.put(url, SceneAssetRelation, httpOptions).pipe(
      tap((SceneAssetRelationRes: SceneAssetRelation) => true ), //console.log()),
      catchError(this.handleError<SceneAssetRelation>('updateSceneAssetRelation'))
    )
  }

  updateAudioSceneAssetRelation(id: number, AudioSceneAssetRelation: any): Observable<AudioSceneAssetRelation> {
    const url = `${apiUrl}/PutAudioSceneAssetRelation/${id}`;
    return this.http.put(url, AudioSceneAssetRelation, httpOptions).pipe(
      tap((AudioSceneAssetRelationRes: AudioSceneAssetRelation) => true ), //console.log()),
      catchError(this.handleError<AudioSceneAssetRelation>('updateAudioSceneAssetRelation'))
    )
  }

  updateModel3DSceneAssetRelation(id: number, ModelSceneAssetRelation: Model3DSceneAssetRelation) {
    const url = `${apiUrl}/PutModel3DSceneAssetRelation/${id}`;
    return this.http.put(url, ModelSceneAssetRelation, httpOptions).pipe(
      tap((ModelSceneAssetRelationRes: Model3DSceneAssetRelation) => true ), //console.log()),
      catchError(this.handleError<Model3DSceneAssetRelation>('updateModel3DSceneAssetRelation'))
    )
  }

  updateInvisibleSceneAssetRelation(id: number, InvisibleSceneAssetRelation: InvisibleSceneAssetRelation) {
    const url = `${apiUrl}/PutInvisibleSceneAssetRelation/${id}`;
    return this.http.put(url, InvisibleSceneAssetRelation, httpOptions).pipe(
      tap((InvisibleSceneAssetRelationRes: InvisibleSceneAssetRelation) => true ), //console.log()),
      catchError(this.handleError<InvisibleSceneAssetRelation>('updateInvisibleSceneAssetRelation'))
    )
  }

  updateImageSceneAssetRelation(id: number, ImageSceneAssetRelation: ImageSceneAssetRelation) {
    const url = `${apiUrl}/PutImageSceneAssetRelation/${id}`;
    return this.http.put(url, ImageSceneAssetRelation, httpOptions).pipe(
      tap((ImageSceneAssetRelationRes: ImageSceneAssetRelation) => true ), //console.log()),
      catchError(this.handleError<ImageSceneAssetRelation>('updateImageSceneAssetRelation'))
    )
  }

  updateVideoSceneAssetRelation(id: number, VideoSceneAssetRelation: VideoSceneAssetRelation) {
    const url = `${apiUrl}/PutVideoSceneAssetRelation/${id}`;
    return this.http.put(url, VideoSceneAssetRelation, httpOptions).pipe(
      tap((VideoSceneAssetRelationRes: VideoSceneAssetRelation) => true ), //console.log()),
      catchError(this.handleError<VideoSceneAssetRelation>('updateVideoSceneAssetRelation'))
    )
  }

  updateQuestionSceneAssetRelation(id: number, QuestionSceneAssetRelation: QuestionSceneAssetRelation) {
    const url = `${apiUrl}/PutQuestionSceneAssetRelation/${id}`;
    return this.http.put(url, QuestionSceneAssetRelation, httpOptions).pipe(
      tap((QuestionSceneAssetRelationRes: QuestionSceneAssetRelation) => true ), //console.log()),
      catchError(this.handleError<QuestionSceneAssetRelation>('updateQuestionSceneAssetRelation'))
    )
  }

  deleteSceneAssetRelation(id: number): Observable<SceneAssetRelation> {
    const url = `${apiUrl}/DeleteSceneAssetRelation/${id}`;
    return this.http.delete<SceneAssetRelation>(url, httpOptions).pipe();
  }

  sceneAssetRelationExists(id: number): Observable<boolean> {
    const url = `${apiUrl}/SceneAssetRelationExists/${id}`;
    return this.http.get<boolean>(url, httpOptions).pipe();
  }

  getSceneAssetRelationsWithSceneIds(sceneIds: number[]): Observable<SceneAssetRelation[]> {
    const url = apiUrl + '/GetSceneAssetRelationsWithSceneIds';
    return this.http.post<SceneAssetRelation[]>(url, sceneIds, httpOptions).pipe();
  }

  getSceneAssetRelationsWithSceneId(sceneId: number): Observable<SceneAssetRelation[]> {
    const url = `${apiUrl}/GetSceneAssetRelationsWithSceneId/${sceneId}`;
    return this.http.get<SceneAssetRelation[]>(url).pipe();
  }

  GetSceneAssetRelationsWithSceneIdAssetRelationId(sceneId: number, assetRelationId: number) {
    const url = `${apiUrl}/GetSceneAssetRelationsWithSceneIdAssetRelationId/${sceneId}/${assetRelationId}`;
    return this.http.get<SceneAssetRelation>(url).pipe();
  }

  addRelation(sceneAssetRelation: SceneAssetRelation, assetType: any): Observable<SceneAssetRelation> {
    var objToUpdate: any = {};
    switch (assetType) {
      case 'Text':
      case 'Audio':
      case 'HotSpot':
      case 'QuestionSet': {
        return this.addSceneAssetRelation(sceneAssetRelation);
      }
      case 'Image':
        let imageSceneAsseRelation: ImageSceneAssetRelation = sceneAssetRelation as ImageSceneAssetRelation;
        return this.addImageSceneAssetRelation(imageSceneAsseRelation);
      case 'Video':
        let videoSceneAsseRelation: VideoSceneAssetRelation = sceneAssetRelation as VideoSceneAssetRelation;
        return this.addVideoSceneAssetRelation(videoSceneAsseRelation);
      case 'Model3D': {
        let model3dSceneAsseRelation: Model3DSceneAssetRelation = sceneAssetRelation as Model3DSceneAssetRelation;
        return this.addModel3DSceneAssetRelation(model3dSceneAsseRelation);
      }
      case 'Invisible': {
        let invisibleSceneAsseRelation: InvisibleSceneAssetRelation = sceneAssetRelation as InvisibleSceneAssetRelation;
        return this.addInvisibleSceneAssetRelation(invisibleSceneAsseRelation);
      }
      case 'Question': {
        let questionSceneAsseRelation: QuestionSceneAssetRelation = sceneAssetRelation as QuestionSceneAssetRelation;
        return this.addQuestionSceneAssetRelation(questionSceneAsseRelation);
      }
    }
  }

  updateRelation(sceneAssetRelation: SceneAssetRelation, assetType: any): Observable<SceneAssetRelation> {
    switch (assetType) {
      case 'Text':
      case 'Audio':
      case 'HotSpot':
      case 'QuestionSet': {
        return this.updateSceneAssetRelation(sceneAssetRelation.sceneAssetRelationId, sceneAssetRelation);
      }
      case 'Image':
        let imageSceneAsseRelation: ImageSceneAssetRelation = sceneAssetRelation as ImageSceneAssetRelation;
        return this.updateImageSceneAssetRelation(imageSceneAsseRelation.sceneAssetRelationId, imageSceneAsseRelation);
      case 'Video':
        let videoSceneAsseRelation: VideoSceneAssetRelation = sceneAssetRelation as VideoSceneAssetRelation;
        return this.updateVideoSceneAssetRelation(videoSceneAsseRelation.sceneAssetRelationId, videoSceneAsseRelation);
      case 'Model3D': {
        let model3dSceneAsseRelation: Model3DSceneAssetRelation = sceneAssetRelation as Model3DSceneAssetRelation;
        return this.updateModel3DSceneAssetRelation(model3dSceneAsseRelation.sceneAssetRelationId, model3dSceneAsseRelation);
      }
      case 'Invisible': {
        let invisibleSceneAsseRelation: InvisibleSceneAssetRelation = sceneAssetRelation as InvisibleSceneAssetRelation;
        return this.updateInvisibleSceneAssetRelation(invisibleSceneAsseRelation.sceneAssetRelationId, invisibleSceneAsseRelation);
      }
      case 'Question': {
        let questionSceneAsseRelation: QuestionSceneAssetRelation = sceneAssetRelation as QuestionSceneAssetRelation;
        return this.updateQuestionSceneAssetRelation(questionSceneAsseRelation.sceneAssetRelationId, questionSceneAsseRelation);
      }
    }
  }
}
